import { toNumberIfString } from '@/core/common.js'

/**
 * 現在地まわり
 * 
 * #CurrentLocation のボタンをマップ上に設置
 * 
 * 現在地を取得前は以下を表示
 * .ready
 * 
 * 取得中は以下を表示
 * .loading
 * 
 * 取得後は以下をつける（表示する要素は.ready）
 * .completed
 * 
 * TODO 地図の表示範囲内から離れたら最初の状態に戻してもいい
 * 
 * NOTE: 現在地ボタンは2箇所で実装（このLocationManager: 右下アイコン、CurrentLocation.vue: 検索メニュー内）
 * 同じ処理を独立して実装しているため、状態の同期は行わない
 */
export class LocationManager {
  /**
   * Geolocation API の結果コード：位置の取得拒否
   */
  static CURRENT_LOCATION_RESULT_DENIED = 1

  /**
   * Geolocation API の結果コード：タイムアウト
   */
  static CURRENT_LOCATION_RESULT_TIMEOUTED = 3


  /**
   * 現在地取得のタイムアウト[ms]
   */
  static TIMEOUT = 15 * 1000

  /**
   * 現在地キャッシュの期間 [ms]
   * NOTE: 一部環境（Android Chrome、端末依存の可能性も）では短い間隔でgetCurrentPositionを呼ぶと
   * 位置情報が長時間取得できなくなることがあるため、短時間のキャッシュを利用する
   */
  static MAXIMUM_AGE = 60 * 1000 // 60秒

  /**
   * @param {google.maps.Map} map 
   */
  constructor(map, options) {
    /**
     * @type {google.maps.Map}
     */
    this.map = map

    /**
     * 現在地取得中表現の最小時間[ms]
     */
    this.MIN_DURATION = 500

    /**
     * true: 読込中
     */
    this.isLoading = false

    /**
     * 現在地取得の開始時刻
     * Date.now()
     */
    this.started = null

    /**
     * 現在地マーカー
     */
    this.marker = null

    // マップに設置
    const button = document.createElement('div')
    button.id = 'CurrentLocation'
    button.innerHTML = `
      <svg class="ready" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18px" height="18px"><path d="M0 0h24v24H0z" fill="none"/><path stroke-width="1" d="M12 8c-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4-1.79-4-4-4zm8.94 3c-.46-4.17-3.77-7.48-7.94-7.94V1h-2v2.06C6.83 3.52 3.52 6.83 3.06 11H1v2h2.06c.46 4.17 3.77 7.48 7.94 7.94V23h2v-2.06c4.17-.46 7.48-3.77 7.94-7.94H23v-2h-2.06zM12 19c-3.87 0-7-3.13-7-7s3.13-7 7-7 7 3.13 7 7-3.13 7-7 7z"/></svg>
      <svg class="loading" xmlns="http://www.w3.org/2000/svg" height="24" viewBox="0 -960 960 960" width="24"><path d="M480-80q-82 0-155-31.5t-127.5-86Q143-252 111.5-325T80-480q0-83 31.5-155.5t86-127Q252-817 325-848.5T480-880q17 0 28.5 11.5T520-840q0 17-11.5 28.5T480-800q-133 0-226.5 93.5T160-480q0 133 93.5 226.5T480-160q133 0 226.5-93.5T800-480q0-17 11.5-28.5T840-520q17 0 28.5 11.5T880-480q0 82-31.5 155t-86 127.5q-54.5 54.5-127 86T480-80Z"/></svg>
      `
    button.onclick = async () => {
      // console.debug('CurrentLocationButton clicked')
      if (!options.onClicked) {
        return
      }

      await options.onClicked()
    }

    this.map.controls[google.maps.ControlPosition.RIGHT_BOTTOM].push(button)
  }

  /**
   * 地図移動時の更新
   * 
   * 現在地が表示範囲外に出ていたら、取得前の状態に戻す
   * 範囲内に戻ったら取得済にする
   * 
   * NOTE: CurrentLocation.vueのupdateCurrentLocationVisibility()と同じ処理を独立実装
   */
  update() {
    if (!this.location) {
      return
    }

    const target = document.getElementById("CurrentLocation")

    if (target && this.map.getBounds().contains(this.location)) {
      target.classList.add("completed")

      return
    }

    if (target) {
      target.classList.remove("loading")
      target.classList.remove("completed")
    }
  }

  /**
   * 現在地を取得して移動
   * 精度が低い場合は採用しない
   * @param {*} getOnly true = 現在地を取得するだけで移動しない
   * @resolve google.maps.LatLng
   * @reject {Error}
   * @returns {Promise}
   * resolve: google.maps.LatLng
   * reject: { 例外そのもの＋取れた時は精度
   *   code: number コード
   *   message: string メッセージ
   *   accuracy: number 精度
   * }
   * 
   * NOTE: CommonService.panToCurrentLocation()と同じ処理を独立実装
   */
  async panToCurrentLocation(getOnly = false) {
    this.onBeforeGetLocation()

    // 取得中表示の最小時間を確保する準備
    let target = document.getElementById("CurrentLocation")
    if (target) {
      target.classList.add("loading")
    }
    this.started = Date.now()
    this.isLoading = true

    /**
     * ローディングを解除する
     * @return {void}
     */
    const finalize = () => {
      this.onAfterGetLocation()
      const target = document.getElementById("CurrentLocation")
      if (target) {
        target.classList.remove("loading")
      }

      this.isLoading = false
    }

    try {
      const location = await LocationManager.getCurrentPosition()

      // ローディング表示の最小時間を確保
      const diff = Date.now() - this.started
      if (diff < this.MIN_DURATION) {
        // ローディング表示を最低でも MIN_DURATION の時間は表示
        const wait = this.MIN_DURATION - diff
        await new Promise(resolve => setTimeout(resolve, wait))
      }

      finalize()
      this.started = null
      // 位置を保存
      this.location = location

    } catch (error) {
      finalize()
      this.onFailedGetLocation(error)
      throw error
    }

    // UI更新とマップ操作
    if (!getOnly) {
      this.setCurrentLocationMarker(this.location)
      this.map.panTo(this.location)
    }

    target = document.getElementById("CurrentLocation")
    if (target) {
      target.classList.add("completed")
    }

    this.onSucceededGetLocation(this.location)
    return this.location
  }

  /**
   * マーカーとして設置
   * @param {google.maps.Map} position 現在地
   */
  setCurrentLocationMarker(position) {
    if (!this.marker) {
      const image = document.createElement("div")
      image.innerHTML = '<img src="/assets/images/self.svg">'

      this.marker = new google.maps.marker.AdvancedMarkerElement({
        map: this.map,
        title: storelocator.messages?.common_current_location || "Current Location",
        content: image
      })
    }

    this.marker.position = {
      lat: position.lat(),
      lng: position.lng()
    }
  }

  /**
   * 現在地取得前
   * フック用
   */
  onBeforeGetLocation() {}

  /**
   * 現在地取得後
   * 取得成功とは限らない
   * フック用
   */
  onAfterGetLocation() {}

  /**
   * 現在地取得に成功し、移動した後
   * フック用
   * @param {google.maps.LatLng} location
   */
  onSucceededGetLocation(location) {}

  /**
   * 現在地取得に失敗した後
   * フック用
   * @param {Error} e
   */
  onFailedGetLocation(e) {}

  /**
   * 現在地を取得し、GoogleMapsの緯度軽度として返す
   * 
   * @param {Object} options オプション
   * @param {boolean} options.enableHighAccuracy 高精度モード（デフォルト: false）
   * @param {number} options.timeout タイムアウト時間（ms）（デフォルト: LocationManager.TIMEOUT）
   * @param {number} options.maximumAge キャッシュ許容時間（ms）（デフォルト: LocationManager.MAXIMUM_AGE）
   * @param {number} options.maxAccuracy 許容精度（m）（デフォルト: 100000）
   * @returns {Promise<google.maps.LatLng>} 現在地の座標
   * @throws {Object} エラーオブジェクト { code, message, accuracy? }
   */
  static async getCurrentPosition(options = {}) {
    const config = {
      enableHighAccuracy: false,
      timeout: LocationManager.TIMEOUT,
      maximumAge: LocationManager.MAXIMUM_AGE,
      maxAccuracy: 100000,
      ...options
    }

    // geolocationが存在しない場合は実行せずエラーを返す
    if (!navigator.geolocation) {
      const errorMessage = 'Geolocation is not supported'
      console.warn(errorMessage)
      
      // アナリティクス送信
      if (storelocator.analytics?.client) {
        storelocator.analytics.client.send('GetCurrentLocation', {
          Status: 'failed',
          Message: errorMessage
        })
      }
      
      throw {
        code: -1,
        message: errorMessage
      }
    }

    return new Promise((resolve, reject) => {
      // 完了フラグ、タイムアウトの二重処理防止用
      let completed = false

      // 現在地取得がタイムアウトした際のエラー処理
      const handleTimeout = () => {
        if (!completed) {
          completed = true

          // GeolocationAPIのエラー風reject用オブジェクト
          const timeoutError = {
            code: 3,
            message: 'Timeout exceeded (fallback)',
          }
          
          // アナリティクス送信
          if (storelocator.analytics?.client) {
            storelocator.analytics.client.send('GetCurrentLocation', {
              Status: 'failed',
              Message: timeoutError.message
            })
          }
          
          reject(timeoutError)
        }
      }

      /**
       * getCurrentPositionと切り離したタイムアウトを設定、同時に呼ばれるのを避けるため、内部タイムアウトより1秒長く設定。
       * NOTE: getCurrentPositionのTimeoutオプションは特定の環境で処理されず、ローディングが消えなくなる。
       *       そのため、こちらでタイムアウトを設定してローディングを消すようにしている。
       *       例: AndroidのWebViewで、アプリかOSがユーザーにGPSアクセス許可の処理をしなかった場合
       */
      const timeoutId = setTimeout(handleTimeout, config.timeout + 1000)

      navigator.geolocation.getCurrentPosition(
        (position) => {
          if (completed) {
            return
          }
          completed = true
          clearTimeout(timeoutId)

          // 精度チェック
          if (position.coords.accuracy > config.maxAccuracy) {
            console.warn('over accuracy, cannot use position', position.coords.accuracy)
            reject({
              accuracy: position.coords.accuracy
            })
            return
          }

          const location = new google.maps.LatLng(
            position.coords.latitude, 
            position.coords.longitude
          )

          // アナリティクス送信
          if (storelocator.analytics?.client) {
            storelocator.analytics.client.context['CurrentLocationStatus'] = true
            
            storelocator.analytics.client.send('GetCurrentLocation', {
              Status: 'succeeded'
            })
          }

          resolve(location)
        },
        (error) => {
          if (completed) {
            return
          }
          completed = true
          clearTimeout(timeoutId)
          
          console.warn(error)
          
          // アナリティクス送信
          if (storelocator.analytics?.client) {
            storelocator.analytics.client.send('GetCurrentLocation', {
              Status: 'failed',
              Message: error.message
            })
          }
          
          reject(error)
        },
        {
          enableHighAccuracy: config.enableHighAccuracy,
          timeout: config.timeout,
          maximumAge: config.maximumAge,
        }
      )
    })
  }
}

/**
 * 位置のキャッシュ
 * ローカルストレージの保存・復元を担当
 * 移動までは担当しない
 */
export class LocationCache {
  constructor() {
    this.key = `storelocator:${location.host}:location`

    /**
     * 初期値
     * かつキャッシュの復元先になる
     */
    this.default = {
      lat: 35.6812362,
      lng: 139.7671248,
      zoom: 16
    }

    /**
     * 最小ズームレベル
     * 今は使えてない
     */
    this.minZoom = 11

    /**
     * true: キャッシュから復元したことを示す
     * 現在地への移動を行っていいかどうかの判断に使う
     */
    this.loadedCache = false
  }

  /**
   * 表示位置・ズームをキャッシュ
   * @param {google.maps.LatLng} position map.getCenter()
   * @param {Number} zoom map.getZoom()
   */
  save(position, zoom) {
    if (!storelocator.map?.cache?.enabled) {
      return
    }

    if (localStorage) {
      localStorage.setItem(
        this.key,
        JSON.stringify({
          lat: position.lat(),
          lng: position.lng(),
          zoom: zoom,
          expireTime: Math.floor( new Date().getTime() / 1000 )
            + (storelocator.map?.cache?.lifetime ? storelocator.map?.cache?.lifetime : 180)
        })
      )
    }
  }

  /**
   * 位置のキャッシュを読み込み
   * @returns {Object} {lat, lng, zoom}
   */
  load() {
    if (!localStorage) {
      return undefined
    }

    const value = localStorage.getItem(this.key)
    if (value == undefined) {
      return undefined
    }

    return JSON.parse(value)
  }

  /**
   * 位置をキャッシュから復元
   * デフォルト値や上限値のみ変更、移動までは行わない
   * 期限切れのキャッシュは破棄する
   * 
   * 優先順位に一貫性がないようにも見える、要確認
   * 
   * @returns {LocationCache}
   */
  restore() {
    let cache = this.load()

    if (cache) {
      // 期限切れのキャッシュなら破棄
      const date = new Date()
      const now = date.getTime() / 1000
  
      if (now > parseInt(cache.expireTime)) {
        console.info('expired cache, remove cache', now, cache.expireTime)
        localStorage.removeItem(this.key)
      }

      // 開き直しや外部からのアクセスであればキャッシュは使わない
      // console.debug('referrer', document.referrer, location.host)
      if (!document.referrer || document.referrer.indexOf(location.host) === -1) {
        // console.debug('reopen or from external, remove cache')
        localStorage.removeItem(this.key)
        cache = undefined
      }
    }

    if (cache) {
      this.loadedCache = true
    }

    const config = storelocator.map?.location?.default

    if (config && cache != undefined) {
      this.default.lat = toNumberIfString(cache.lat)
      this.default.lng = toNumberIfString(cache.lng)
    } else if (config?.latitude != null && config?.longitude != null) {
      this.default.lat = toNumberIfString(config.latitude)
      this.default.lng = toNumberIfString(config.longitude)
    }

    if (config && cache != undefined) {
      this.default.zoom = toNumberIfString(cache.zoom)
    } else if (config?.zoom != null) {
      this.default.zoom = toNumberIfString(config.zoom)
    }

    if (config?.min_zoom != undefined) {
      this.minZoom = toNumberIfString(config.min_zoom)
    }

    return this
  }
}